import type { AlignmentChoice } from '../../schemas/alignment';
import type { Bond } from '../../schemas/bond';
import {
  CHARACTER_SCHEMA_VERSION,
  type ChosenAspetto,
  type MoveRef,
  type SpellRef,
} from '../../schemas/character';
import type { ClassId } from '../../schemas/classId';
import type { Item } from '../../schemas/equipment';
import type { RaceChoice } from '../../schemas/race';
import type { Stats } from '../../schemas/stats';

/**
 * Bozza di PG durante la creazione (interno alla feature character-builder).
 *
 * Stessa "forma" del Character ma con quasi tutti i campi opzionali, perché
 * il giocatore li riempie progressivamente nel corso del wizard. I campi
 * derivati (`hp`, `load`, `damage`, `armor`) NON sono qui — vengono calcolati
 * solo al finalize via `services/characterCreation::finalizeCharacter`.
 *
 * **Versione**: la `schemaVersion: 2` rispecchia la versione del Character
 * di destinazione. Se mai bumperemo CharacterSchema a v3 dovremo decidere
 * se anche il draft cambia formato e prevedere una migration interna alla
 * feature (rara — i draft sono effimeri).
 */
export interface CharacterDraft {
  schemaVersion: typeof CHARACTER_SCHEMA_VERSION;
  /** UUID generato all'inizio del wizard. Identifica il draft tra refresh. */
  id: string;
  // I campi opzionali usano `| undefined` esplicito perché il tsconfig ha
  // `exactOptionalPropertyTypes: true`: con il solo `?` non potremmo
  // assegnare `undefined` (per resettare un campo dopo SET_CLASS).
  name?: string | undefined;
  title?: string | undefined;
  classId?: ClassId | undefined;
  archetypeId?: string | undefined;
  race?: RaceChoice | undefined;
  alignment?: AlignmentChoice | undefined;
  stats?: Stats | undefined;
  chosenAspetti: ChosenAspetto[];
  moves: MoveRef[];
  /**
   * Incantesimi selezionati dal PG (sia auto-aggiunti come i trucchetti del
   * Mago, sia scelti dal giocatore tra quelli disponibili). La sezione
   * `'spells'` del builder lavora su questo array; il finalize lo copia in
   * `Character.spells`.
   */
  spells: SpellRef[];
  inventory: Item[];
  bonds: Bond[];
  /**
   * Se `true`, il giocatore ha posticipato la compilazione dei legami alla
   * prima sessione di gioco. La sezione `'identity-bonds'` si considera
   * completa anche con `bonds.length === 0` (FI-25).
   */
  bondsDeferred?: boolean | undefined;
  notes: string;
  /** ISO timestamp di creazione del draft. */
  createdAt: string;
}

/**
 * Crea un draft vuoto, da chiamare all'avvio di una nuova creazione PG.
 */
export function createEmptyDraft(): CharacterDraft {
  return {
    schemaVersion: CHARACTER_SCHEMA_VERSION,
    id: crypto.randomUUID(),
    chosenAspetti: [],
    moves: [],
    spells: [],
    inventory: [],
    bonds: [],
    notes: '',
    createdAt: new Date().toISOString(),
  };
}

/**
 * Identificatori delle sezioni dell'accordion del builder.
 *
 * L'ordine in {@link BUILDER_SECTION_ORDER} riflette la sequenza in cui
 * le sezioni si auto-aprono (D5 del piano), ma il layout pagina-accordion
 * (D1) consente al giocatore di tornare a qualsiasi sezione precedente in
 * qualunque momento.
 */
export type BuilderSectionId =
  | 'class'
  | 'race'
  | 'archetype'
  | 'stats'
  | 'alignment'
  | 'aspetti'
  | 'equipment'
  | 'starting-moves'
  | 'spells'
  | 'identity-bonds';

export const BUILDER_SECTION_ORDER: readonly BuilderSectionId[] = [
  'class',
  'race',
  'archetype',
  'stats',
  'alignment',
  'aspetti',
  'equipment',
  'starting-moves',
  'spells',
  'identity-bonds',
];

/**
 * Stato globale del builder gestito dal reducer.
 *
 * Niente stato derivato qui (es. "quali sezioni sono complete"): è calcolato
 * via selettori `isSectionComplete` per restare SSOT sul `draft`.
 */
export interface BuilderState {
  draft: CharacterDraft;
}

/**
 * Tutte le mutazioni del draft passano per un'azione tipizzata. Ogni step
 * dell'accordion fa `dispatch({ type: '...', ... })` invece di mutare
 * direttamente lo state.
 *
 * Le azioni di "set globale" (`SET_CLASS`, `SET_RACE`) ovverridano i campi;
 * le azioni di append/toggle (mosse, aspetti, equip) sono additive con
 * semantica esplicita sul reducer.
 */
export type BuilderAction =
  | { type: 'INIT_DRAFT'; draft: CharacterDraft }
  | { type: 'SET_NAME'; name: string }
  | { type: 'SET_TITLE'; title: string | undefined }
  | { type: 'SET_CLASS'; classId: ClassId }
  | { type: 'SET_RACE'; race: RaceChoice | undefined }
  | { type: 'SET_ARCHETYPE'; archetypeId: string }
  | { type: 'SET_STATS'; stats: Stats }
  | { type: 'CLEAR_STATS' }
  | { type: 'SET_ALIGNMENT'; alignment: AlignmentChoice }
  | { type: 'SET_ASPETTI'; chosenAspetti: ChosenAspetto[] }
  | { type: 'SET_INVENTORY'; inventory: Item[] }
  | { type: 'SET_MOVES'; moves: MoveRef[] }
  | { type: 'SET_MOVE_CHOICE_GROUP'; moveId: string; groupId: string; selectedIds: string[] }
  | { type: 'SET_MOVE_NARRATIVE_FIELD'; moveId: string; fieldId: string; value: string }
  | { type: 'SET_SPELLS'; spells: SpellRef[] }
  | { type: 'SET_BONDS'; bonds: Bond[] }
  | { type: 'MARK_BONDS_DEFERRED'; bondsDeferred: boolean }
  | { type: 'SET_NOTES'; notes: string }
  | { type: 'RESET_DRAFT' };
